import type { FC, ReactElement } from 'react';
import { useEffect, useRef, useState } from 'react';

import { useIsLandscape, useIsSmallerThanWide } from '../../styles/hooks/media';
import { Button, ButtonVariant } from '../controls/button/Button';
import type { ISwiperHandle } from '../swiper/Swiper';
import { Swiper } from '../swiper/Swiper';

import styles from './QuickGuide.module.css';

export interface QuickGuidePlainProps {
  isOpen: boolean;
  listItems: ReactElement[];
  onPlayAction: () => void;
}

export const QuickGuidePlain: FC<QuickGuidePlainProps> = ({ isOpen, listItems, onPlayAction }): ReactElement => {
  const [activeIndex, setActiveIndex] = useState(0);

  const containerRef = useRef<HTMLDivElement>(null);
  const horizontalScrollRef = useRef<ISwiperHandle>(null);

  const isLandscape = useIsLandscape();
  const isMobile = useIsSmallerThanWide();
  const smallVariant = isLandscape && isMobile;

  useEffect(() => {
    if (isOpen) {
      setActiveIndex(0);
    }
  }, [isOpen]);

  const handlePrevious = (): void => {
    horizontalScrollRef.current?.goTo('previous');
  };

  const handleNext = (): void => {
    horizontalScrollRef.current?.goTo('next');
  };

  return (
    <div className={styles.quickGuide} ref={containerRef}>
      <div className={styles.quickGuide__content}>
        <Swiper
          className={styles.quickGuide__slider}
          dots={!smallVariant && listItems.length > 1}
          onActiveChange={(index) => setActiveIndex(index)}
          ref={horizontalScrollRef}
        >
          {listItems}
        </Swiper>
      </div>
      <div className={styles.quickGuide__footer}>
        {listItems.length === 1 ? (
          <Button onClick={onPlayAction} tk="PLAY" type="button" variant={ButtonVariant.ACCENT} />
        ) : (
          <>
            <Button
              disabled={activeIndex === 0}
              onClick={handlePrevious}
              tk="PREVIOUS"
              type="button"
              variant={ButtonVariant.MAIN}
            />
            {smallVariant ? (
              <div className={styles.quickGuide__slideCounter}>
                {activeIndex + 1}/{listItems.length}
              </div>
            ) : null}
            {activeIndex === listItems.length - 1 ? (
              <Button onClick={onPlayAction} tk="PLAY" type="button" variant={ButtonVariant.ACCENT} />
            ) : (
              <Button
                data-testid="quick-guide-next-button"
                onClick={handleNext}
                tk="NEXT"
                type="button"
                variant={ButtonVariant.ACCENT}
              />
            )}
          </>
        )}
      </div>
    </div>
  );
};
